import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { PayBy, PayOn } from '@/utils/Enumerations'
import JSZip from 'jszip'
import saveAs from 'file-saver'
import { generateSvgForPdf, deductionIcon, deductionNegativeIcon } from '@/images/AngelSvgLibrary'
import i18n from '@/i18n'
import { fileNameSafeString, appendDateToFileName } from '@/utils/Files'
import { getFooter, getMargin } from '@/utils/pdf-formatters/pdfHelpers'
import { getRateSheetPage } from '@/utils/pdf-formatters/RateSheet'
import {
  baseRateText,
  contentTypeCode,
  detailRateText,
  distanceSuffix,
  documentStyles,
  getRateSheets,
  gridRowFillLayout,
  headerBlock,
  infoBlock,
  sortByActivityName,
  sortByProductName
} from '@/utils/pdf-formatters/GenericContractFunctions'

PDFMake.vfs = pdfFonts.pdfMake.vfs

export async function generateContractPdf ({ activitiesByAccounts, ...params }) {
  if (activitiesByAccounts.length === 1) {
    const { account, activities } = activitiesByAccounts[0]
    const pdf = await getContractPdf(params, account, activities)
    pdf.download(`${counterpartyFileName(params, account)}.pdf`)
  } else {
    const zip = new JSZip()
    for (const { account, activities } of activitiesByAccounts) {
      const pdf = await getContractPdf(params, account, activities)
      zip.file(`${counterpartyFileName(params, account)}.pdf`, await getBlobFromPdf(pdf))
    }

    saveAs(await zip.generateAsync({ type: 'blob' }), `${contentTypeCode(params.contractMode)}_Contracts`)
  }
}

function counterpartyFileName ({ contractMode, activityPeriod }, account) {
  const base = `${contentTypeCode(contractMode)}_${fileNameSafeString(account.name)}`
  return activityPeriod?.startDate ? `${base}_${activityPeriod.startDate}` : appendDateToFileName(base)
}

const getBlobFromPdf = (pdf) => {
  return new Promise((resolve) => {
    pdf.getBlob(data => {
      resolve(data)
    })
  })
}

async function getContractPdf (params, account, activities) {
  const content = [
    headerBlock(params, { account }),
    infoBlock(params),
    activityGrid(params, activities),
    await rateSheetPages(params, activities)
  ]

  return PDFMake.createPdf({
    content,
    pageOrientation: 'landscape',
    styles: documentStyles,
    footer: documentFooter
  })
}

const gridColumns = [
  { labelKey: 'activity', width: '*' },
  { labelKey: 'product', width: '*' },
  { labelKey: 'payBy', width: 90, alignment: 'center' },
  { labelKey: 'payOn', width: 90, alignment: 'center' },
  { labelKey: 'rate', width: 'auto', alignment: 'right' }
]

function activityGrid (params, activities, topMargin = 25) {
  const { contract } = params
  const headerRow = gridColumns.map(column => ({ text: i18n.t(column.labelKey), style: 'header', alignment: column.alignment }))
  const rows = []

  for (const activity of sortByActivityName(activities)) {
    const distanceFormatted = distanceSuffix(activity, contract)

    const isDeductingActivity = !!activity.deductFromActivityId
    const isDeductedFrom = activities.some(a => a.deductFromActivityId === activity.activityId)

    let activityNameCell
    if (isDeductingActivity) {
      activityNameCell = {
        columns: [
          { svg: generateSvgForPdf(deductionIcon), width: 10 },
          { text: activity.activityTemplate.name, style: 'header', width: '*' }
        ],
        columnGap: 5
      }
    } else if (isDeductedFrom) {
      activityNameCell = {
        columns: [
          { svg: generateSvgForPdf(deductionNegativeIcon), width: 10 },
          { text: activity.activityTemplate.name, style: 'header', width: '*' }
        ],
        columnGap: 3
      }
    } else {
      activityNameCell = { text: activity.activityTemplate.name, style: 'header' }
    }

    rows.push([
      activityNameCell,
      { text: i18n.t('base'), style: 'header' },
      { text: activity.baseCost ? PayBy.fromInt(activity.baseCost.payBy) : i18n.t('notApplicable'), style: 'header' },
      { text: activity.baseCost ? PayOn.fromInt(activity.baseCost.payOn) : i18n.t('notApplicable'), style: 'header' },
      { text: baseRateText(activity, distanceFormatted), style: 'header' }
    ])

    rows.push(...getDetailRows(activity))
  }

  return {
    layout: gridRowFillLayout,
    table: {
      widths: gridColumns.map(column => column.width),
      headerRows: 1,
      body: [headerRow, ...rows.map(row => row.map((cell, index) => ({ ...cell, alignment: gridColumns[index].alignment })))]
    },
    margin: [0, topMargin, 0, 0]
  }
}

function getDetailRows (activity) {
  return sortByProductName(activity.activityDetails).map(d => [
    { text: '', style: 'subtitle' },
    { text: d.product.name, style: 'subtitle' },
    { text: d.cost ? PayBy.fromInt(d.cost.payBy) : i18n.t('notApplicable'), style: 'subtitle' },
    { text: d.cost ? PayOn.fromInt(d.cost.payOn) : i18n.t('notApplicable'), style: 'subtitle' },
    { text: detailRateText(d), style: 'subtitle' }
  ])
}

async function rateSheetPages ({ activityPeriod }, activities) {
  return await Promise.all(getRateSheets(activities)
    .map(rateSheet => getRateSheetPage(rateSheet, activityPeriod)))
}

const documentFooter = function (currentPage) {
  return {
    ...getFooter(currentPage,
      false,
      getMargin({ left: 30, top: 0, right: 30, bottom: 10 }),
      10),
    style: 'subtitle'
  }
}
